import { useCallback, useMemo } from 'react';
import { useMatches } from 'react-router-dom';
import { StrictRouteMatch } from 'src/types/reactRouter';
import { useClientId } from 'src/contexts/ClientContext';
import { getDeviceType } from 'src/utils/deviceDetection';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { safePublish } from './safePublish';
import { FeatureName, ActionType } from './metricsFields';

export interface UserMetricEvent {
  // The feature which was interacted with by the user
  feature: FeatureName;
  // Optional additional metadata to publish within the same action (e.g., url of a link clicked)
  metadata?: Record<string, string | number | undefined>;
}

const getPublisherMethodName = (pageName: FeatureName, actionType: ActionType) => (
  `${pageName}.${actionType}`
);

/**
 * This hook is used to publish metrics for user events with contextual awareness of the current page.
 * All user events should be published through this hook.
 */
export const useMetrics = () => {
  const matches = useMatches() as StrictRouteMatch[];
  const activeRoute: StrictRouteMatch = matches[matches.length - 1];
  const { pageName } = activeRoute.handle;
  const clientId = useClientId();

  const publishMetric = useCallback((event: UserMetricEvent, actionType: ActionType) => {
    safePublish(
      getPublisherMethodName(pageName, actionType),
      (publisher) => {
        publisher.publishCounterMonitor(event.feature, 1);

        const metadata = { ...event.metadata, clientId, deviceType: getDeviceType() };
        Object.entries(metadata).forEach(([key, value]) => {
          if (key && value != null) publisher.publishStringTruncate(key, value);
        });
      },
      (e) => {
        ClientLogger.warn(ClientLog.MetricsFailure, { feature: event.feature, error: String(e) });
      },
    );
  }, [pageName, clientId]);

  const clickMetric = useCallback(
    (event: UserMetricEvent) => publishMetric(event, ActionType.Click),
    [publishMetric],
  );

  const viewMetric = useCallback(
    (event: UserMetricEvent) => publishMetric(event, ActionType.View),
    [publishMetric],
  );

  return useMemo(() => ({ clickMetric, viewMetric }), [clickMetric, viewMetric]);
};

export default useMetrics;
